// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { HealthcareService, Patient, Practitioner, PractitionerRole, Schedule, Slot } from '@medplum/fhirtypes';
import type { AppointmentTypeDefinition, ProviderRoleCode } from '@polarissleep/scheduling';
import { POLARIS_PROVIDER_ROLE_SYSTEM } from '@polarissleep/scheduling';
import {
  POLARIS_ENV_TAG_SYSTEM,
  SCHEDULING_PARAMETERS_EXTENSION_URL,
  SERVICE_TYPE_REFERENCE_EXTENSION_URL,
} from './constants';
import { SCHEDULING_ERROR_CATEGORIES, SchedulingError } from './errors';
import { APPOINTMENT_TYPE_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM } from './healthcare-service-projection';
import {
  validateResourceScope,
  validateSchedulingScope,
  validateSchedulingTarget,
  validateScopedResource,
} from './tenant';
import { formatFhirDateTimeInTimezone, getTimezoneExtensionValue, isValidTimezone, parseFhirDateTime } from './time';
import type { ProposedAppointment, SchedulingClient, SchedulingScope, SchedulingTarget } from './types';

export interface AvailabilitySearchRepository {
  searchResourcePages(
    resourceType: 'HealthcareService',
    search: Record<string, string>
  ): AsyncIterable<HealthcareService[]>;
  searchResourcePages(
    resourceType: 'PractitionerRole',
    search: Record<string, string>
  ): AsyncIterable<PractitionerRole[]>;
  searchResourcePages(resourceType: 'Practitioner', search: Record<string, string>): AsyncIterable<Practitioner[]>;
  searchResourcePages(resourceType: 'Schedule', search: Record<string, string>): AsyncIterable<Schedule[]>;
}

export interface AvailabilitySearchRequest {
  appointmentType: AppointmentTypeDefinition;
  start: string;
  end: string;
  scope: SchedulingScope;
  patient?: Patient;
  practitionerId?: string;
  count?: number;
  signal?: AbortSignal;
}

export interface AvailabilityPolicyContext {
  appointmentType: AppointmentTypeDefinition;
  patient?: Patient;
  scope: SchedulingScope;
  start: Date;
  end: Date;
  now: Date;
  provider?: EligibleAvailabilityProvider;
}

export interface AvailabilitySearchPolicies {
  /** Returns earliest UTC instant that may be booked. Defaults to now. */
  minimumNotice?: (context: AvailabilityPolicyContext) => Date | string | Promise<Date | string>;
  /** Returns latest UTC instant that may be booked. Defaults to requested end. */
  bookingHorizon?: (context: AvailabilityPolicyContext) => Date | string | Promise<Date | string>;
  /** Returning false removes provider from this patient's search. */
  patientEligibility?: (context: AvailabilityPolicyContext) => boolean | Promise<boolean>;
  /** Lower values mean lower utilization and rank first after explicit ranking. */
  utilization?: (context: AvailabilityPolicyContext) => number | Promise<number>;
  /** Lower values rank first after preferred-provider ordering. */
  ranking?: (context: AvailabilityPolicyContext) => number | Promise<number>;
  now?: () => Date;
}

export interface EligibleAvailabilityProvider {
  practitioner: Practitioner;
  practitionerRole: PractitionerRole;
  roleCode: ProviderRoleCode;
  healthcareService: HealthcareService;
  schedules: Schedule[];
}

export interface AvailabilitySearchResult {
  appointments: ProposedAppointment[];
  providers: EligibleAvailabilityProvider[];
}

interface ProviderTarget {
  provider: EligibleAvailabilityProvider;
  schedule: Schedule;
  target: SchedulingTarget;
  preferredRank: number;
  ranking: number;
  utilization: number;
  providerOrder: number;
}

interface FindWindow {
  start: Date;
  end: Date;
}

const MAX_FIND_WINDOW_MS = 31 * 24 * 60 * 60 * 1000;

export async function searchAvailability(
  repository: AvailabilitySearchRepository,
  schedulingClient: SchedulingClient,
  request: AvailabilitySearchRequest,
  policies: AvailabilitySearchPolicies = {}
): Promise<AvailabilitySearchResult> {
  validateSchedulingScope(request.scope);
  if (request.patient) {
    validateScopedResource(request.patient, 'Patient', request.scope);
  }
  const requestedStart = parseFhirDateTime(request.start);
  const requestedEnd = parseFhirDateTime(request.end);
  if (requestedStart >= requestedEnd) {
    throw new SchedulingError(
      SCHEDULING_ERROR_CATEGORIES.configurationInvalid,
      'Availability start must be before end.'
    );
  }

  const now = policies.now?.() ?? new Date();
  if (Number.isNaN(now.getTime())) {
    throw new SchedulingError(SCHEDULING_ERROR_CATEGORIES.configurationInvalid, 'Availability policy now is invalid.');
  }
  const baseContext: AvailabilityPolicyContext = {
    appointmentType: request.appointmentType,
    patient: request.patient,
    scope: request.scope,
    start: requestedStart,
    end: requestedEnd,
    now,
  };
  const minimumNotice = await resolvePolicyDate(policies.minimumNotice?.(baseContext), now, 'minimum notice');
  const bookingHorizon = await resolvePolicyDate(
    policies.bookingHorizon?.(baseContext),
    requestedEnd,
    'booking horizon'
  );
  const effectiveStart = new Date(Math.max(requestedStart.getTime(), minimumNotice.getTime()));
  const effectiveEnd = new Date(Math.min(requestedEnd.getTime(), bookingHorizon.getTime()));
  if (effectiveStart >= effectiveEnd) {
    return { appointments: [], providers: [] };
  }

  const healthcareService = await resolveHealthcareService(repository, request.appointmentType, request.scope);
  const providers = await resolveEligibleProviders(repository, request, healthcareService);
  const providerTargets: ProviderTarget[] = [];
  const activeProviders: EligibleAvailabilityProvider[] = [];

  for (const [providerOrder, provider] of providers.entries()) {
    const providerContext: AvailabilityPolicyContext = { ...baseContext, provider };
    if (policies.patientEligibility && !(await policies.patientEligibility(providerContext))) {
      continue;
    }
    const ranking = (await policies.ranking?.(providerContext)) ?? 0;
    const utilization = (await policies.utilization?.(providerContext)) ?? 0;
    if (!Number.isFinite(ranking) || !Number.isFinite(utilization)) {
      throw new SchedulingError(
        SCHEDULING_ERROR_CATEGORIES.configurationInvalid,
        `Availability policy returned a non-finite score for Practitioner/${provider.practitioner.id}.`
      );
    }
    activeProviders.push(provider);
    for (const schedule of provider.schedules) {
      const target: SchedulingTarget = {
        healthcareService,
        schedule,
        actor: provider.practitioner,
        scope: request.scope,
      };
      validateSchedulingTarget(target);
      providerTargets.push({
        provider,
        schedule,
        target,
        preferredRank: provider.roleCode === request.appointmentType.preferredProvider ? 0 : 1,
        ranking,
        utilization,
        providerOrder,
      });
    }
  }

  providerTargets.sort(compareProviderTargets);
  const targetRanks = new Map<string, number>();
  for (const [index, target] of providerTargets.entries()) {
    const providerKey = providerKeyForTarget(target.target);
    if (!targetRanks.has(providerKey)) {
      targetRanks.set(providerKey, index);
    }
  }
  const appointmentsByKey = new Map<string, { appointment: ProposedAppointment; providerRank: number }>();
  for (const providerTarget of providerTargets) {
    const timezone = getProviderTimezone(providerTarget.provider.practitioner);
    for (const window of chunkFindWindow(effectiveStart, effectiveEnd)) {
      const findRequest = {
        start: formatFhirDateTimeInTimezone(window.start, timezone),
        end: formatFhirDateTimeInTimezone(window.end, timezone),
        target: providerTarget.target,
        count: request.count,
      };
      const found = request.signal
        ? await schedulingClient.find(findRequest, { signal: request.signal })
        : await schedulingClient.find(findRequest);
      for (const appointment of found) {
        const appointmentStart = parseFhirDateTime(appointment.start ?? '');
        const appointmentEnd = parseCandidateEnd(appointment.end);
        if (
          appointmentStart < effectiveStart ||
          appointmentStart >= effectiveEnd ||
          appointmentEnd === undefined ||
          appointmentEnd <= appointmentStart ||
          appointmentEnd > effectiveEnd
        ) {
          continue;
        }
        if (
          !isWithinV2Schedule(appointment, providerTarget.schedule, timezone, providerTarget.target.healthcareService)
        ) {
          continue;
        }
        const key = proposedAppointmentKey(appointment, providerTarget.target);
        if (!appointmentsByKey.has(key)) {
          appointmentsByKey.set(key, {
            appointment,
            providerRank: targetRanks.get(providerKeyForTarget(providerTarget.target)) ?? Number.MAX_SAFE_INTEGER,
          });
        }
      }
    }
  }

  const appointments = [...appointmentsByKey.entries()]
    .sort(([leftKey, left], [rightKey, right]) => {
      const startDifference =
        parseFhirDateTime(left.appointment.start ?? '').getTime() -
        parseFhirDateTime(right.appointment.start ?? '').getTime();
      if (startDifference !== 0) {
        return startDifference;
      }
      return left.providerRank - right.providerRank || leftKey.localeCompare(rightKey);
    })
    .map(([, candidate]) => candidate.appointment);

  return { appointments, providers: activeProviders };
}

function isWithinV2Schedule(
  appointment: ProposedAppointment,
  schedule: Schedule,
  timezone: string,
  healthcareService: HealthcareService
): boolean {
  const containedSlots = (appointment.contained ?? []).filter(
    (resource): resource is Slot => resource.resourceType === 'Slot'
  );
  if (
    containedSlots.some((slot) =>
      (slot.meta?.tag ?? []).some(
        (tag) =>
          tag.system === 'https://polarissleep.com/scheduling-engine' && tag.code === 'medplum-v2-availability-override'
      )
    )
  ) {
    return true;
  }

  const parameters = (schedule.extension ?? []).find(
    (extension) =>
      extension.url === SCHEDULING_PARAMETERS_EXTENSION_URL &&
      extension.extension?.some(
        (child) =>
          child.url === 'service' && child.valueReference?.reference === `HealthcareService/${healthcareService.id}`
      )
  );
  const availability = parameters?.extension?.find((extension) => extension.url === 'availability');
  if (!availability) {
    return true;
  }
  if (availability.valueBoolean === false) {
    return false;
  }

  const availableTimes = availability.extension?.filter((extension) => extension.url === 'availableTime') ?? [];
  if (availableTimes.length === 0) {
    return false;
  }
  const start = appointment.start ? new Date(appointment.start) : undefined;
  const end = appointment.end ? new Date(appointment.end) : undefined;
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return false;
  }
  const localParts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(start);
  const weekday = localParts
    .find((part) => part.type === 'weekday')
    ?.value.toLowerCase()
    .slice(0, 3);
  const startMinutes = toLocalMinutes(localParts);
  const endParts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(end);
  const endMinutes = toLocalMinutes(endParts);
  return availableTimes.some((availableTime) => {
    const day = availableTime.extension?.find((extension) => extension.url === 'daysOfWeek')?.valueCode;
    const availableStart = availableTime.extension?.find(
      (extension) => extension.url === 'availableStartTime'
    )?.valueTime;
    const availableEnd = availableTime.extension?.find((extension) => extension.url === 'availableEndTime')?.valueTime;
    if (!day || !availableStart || !availableEnd || day !== weekday) {
      return false;
    }
    return startMinutes >= timeToMinutes(availableStart) && endMinutes <= timeToMinutes(availableEnd);
  });
}

function toLocalMinutes(parts: Array<{ type: string; value: string }>): number {
  const hour = Number(parts.find((part) => part.type === 'hour')?.value);
  const minute = Number(parts.find((part) => part.type === 'minute')?.value);
  return hour * 60 + minute;
}

function timeToMinutes(value: string): number {
  const [hour, minute] = value.slice(0, 5).split(':').map(Number);
  return hour * 60 + minute;
}

function parseCandidateEnd(value: string | undefined): Date | undefined {
  if (!value) {
    return undefined;
  }
  try {
    return parseFhirDateTime(value);
  } catch {
    return undefined;
  }
}

export async function findAvailableAppointments(
  repository: AvailabilitySearchRepository,
  schedulingClient: SchedulingClient,
  request: AvailabilitySearchRequest,
  policies: AvailabilitySearchPolicies = {}
): Promise<ProposedAppointment[]> {
  return (await searchAvailability(repository, schedulingClient, request, policies)).appointments;
}

export function chunkFindWindow(start: Date, end: Date): FindWindow[] {
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start >= end) {
    throw new SchedulingError(SCHEDULING_ERROR_CATEGORIES.configurationInvalid, 'Availability window is invalid.');
  }
  const windows: FindWindow[] = [];
  let cursor = new Date(start);
  while (cursor < end) {
    const next = new Date(Math.min(cursor.getTime() + MAX_FIND_WINDOW_MS, end.getTime()));
    windows.push({ start: new Date(cursor), end: next });
    cursor = next;
  }
  return windows;
}

export async function resolveHealthcareService(
  repository: AvailabilitySearchRepository,
  appointmentType: AppointmentTypeDefinition,
  scope: SchedulingScope
): Promise<HealthcareService> {
  const code = appointmentType.coding.code?.trim();
  if (!code) {
    throw new SchedulingError(SCHEDULING_ERROR_CATEGORIES.configurationInvalid, 'Appointment type code is required.');
  }
  const services = await collectResourcePages(
    repository.searchResourcePages('HealthcareService', {
      identifier: `${APPOINTMENT_TYPE_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM}|${code}`,
      active: 'true',
      _compartment: `Organization/${scope.organizationId}`,
      _tag: `${POLARIS_ENV_TAG_SYSTEM}|${scope.environment}`,
    })
  );
  const scopedServices = services.filter((service) => {
    try {
      validateResourceScope(service, scope, 'HealthcareService');
      return true;
    } catch {
      return false;
    }
  });
  if (scopedServices.length === 0) {
    throw new SchedulingError(
      SCHEDULING_ERROR_CATEGORIES.serviceNotFound,
      `No v2 HealthcareService found for appointment type ${code}.`
    );
  }
  if (scopedServices.length > 1) {
    throw new SchedulingError(
      SCHEDULING_ERROR_CATEGORIES.configurationInvalid,
      `Multiple v2 HealthcareServices found for appointment type ${code}.`
    );
  }
  return scopedServices[0];
}

export async function resolveEligibleProviders(
  repository: AvailabilitySearchRepository,
  request: AvailabilitySearchRequest,
  healthcareService: HealthcareService
): Promise<EligibleAvailabilityProvider[]> {
  const rolesByPractitioner = new Map<
    string,
    Array<{ practitionerRole: PractitionerRole; roleCode: ProviderRoleCode }>
  >();
  for (const roleCode of request.appointmentType.providerTypes) {
    const roleSearch: Record<string, string> = {
      role: `${POLARIS_PROVIDER_ROLE_SYSTEM}|${roleCode}`,
      active: 'true',
      _compartment: `Organization/${request.scope.organizationId}`,
      _tag: `${POLARIS_ENV_TAG_SYSTEM}|${request.scope.environment}`,
    };
    if (request.practitionerId) {
      roleSearch.practitioner = `Practitioner/${request.practitionerId}`;
    }
    const roles = await collectResourcePages(repository.searchResourcePages('PractitionerRole', roleSearch));
    for (const practitionerRole of roles) {
      const practitionerReference = practitionerRole.practitioner?.reference;
      if (!practitionerReference?.startsWith('Practitioner/')) {
        continue;
      }
      const practitionerId = practitionerReference.slice('Practitioner/'.length);
      if (request.practitionerId && practitionerId !== request.practitionerId) {
        continue;
      }
      validateResourceScope(practitionerRole, request.scope, 'PractitionerRole');
      const roleCandidates = rolesByPractitioner.get(practitionerId) ?? [];
      if (
        !roleCandidates.some(
          (candidate) => candidate.practitionerRole.id === practitionerRole.id && candidate.roleCode === roleCode
        )
      ) {
        roleCandidates.push({ practitionerRole, roleCode });
        rolesByPractitioner.set(practitionerId, roleCandidates);
      }
    }
  }

  const providers: EligibleAvailabilityProvider[] = [];
  for (const [practitionerId, roleCandidates] of rolesByPractitioner) {
    const practitioners = await collectResourcePages(
      repository.searchResourcePages('Practitioner', {
        _id: practitionerId,
        _compartment: `Organization/${request.scope.organizationId}`,
        _tag: `${POLARIS_ENV_TAG_SYSTEM}|${request.scope.environment}`,
      })
    );
    const practitioner = practitioners.find((candidate) => candidate.id === practitionerId);
    if (!practitioner) {
      continue;
    }
    validateResourceScope(practitioner, request.scope, 'Practitioner');
    const selectedRole = [...roleCandidates].sort((left, right) => compareProviderRoles(left, right, request))[0];
    const schedules = await resolveProviderSchedules(repository, practitioner, healthcareService, request.scope);
    if (schedules.length === 0) {
      continue;
    }
    providers.push({
      practitioner,
      practitionerRole: selectedRole.practitionerRole,
      roleCode: selectedRole.roleCode,
      healthcareService,
      schedules,
    });
  }
  return providers;
}

async function resolveProviderSchedules(
  repository: AvailabilitySearchRepository,
  practitioner: Practitioner,
  healthcareService: HealthcareService,
  scope: SchedulingScope
): Promise<Schedule[]> {
  const schedules = await collectResourcePages(
    repository.searchResourcePages('Schedule', {
      actor: `Practitioner/${practitioner.id}`,
      active: 'true',
      _compartment: `Organization/${scope.organizationId}`,
      _tag: `${POLARIS_ENV_TAG_SYSTEM}|${scope.environment}`,
    })
  );
  const serviceReference = `HealthcareService/${healthcareService.id}`;
  return schedules.filter((schedule) => {
    try {
      validateResourceScope(schedule, scope, 'Schedule');
    } catch {
      return false;
    }
    const actorReference = schedule.actor?.length === 1 ? schedule.actor[0].reference : undefined;
    if (actorReference !== `Practitioner/${practitioner.id}`) {
      return false;
    }
    const serviceReferences = (schedule.serviceType ?? []).flatMap((serviceType) =>
      (serviceType.extension ?? [])
        .filter((extension) => extension.url === SERVICE_TYPE_REFERENCE_EXTENSION_URL)
        .map((extension) => extension.valueReference?.reference)
    );
    return serviceReferences.includes(serviceReference);
  });
}

function compareProviderTargets(left: ProviderTarget, right: ProviderTarget): number {
  return (
    left.preferredRank - right.preferredRank ||
    left.ranking - right.ranking ||
    left.utilization - right.utilization ||
    left.providerOrder - right.providerOrder ||
    (left.schedule.id ?? '').localeCompare(right.schedule.id ?? '')
  );
}

function compareProviderRoles(
  left: { practitionerRole: PractitionerRole; roleCode: ProviderRoleCode },
  right: { practitionerRole: PractitionerRole; roleCode: ProviderRoleCode },
  request: AvailabilitySearchRequest
): number {
  const preferredRank = (roleCode: ProviderRoleCode): number =>
    roleCode === request.appointmentType.preferredProvider ? 0 : 1;
  return (
    preferredRank(left.roleCode) - preferredRank(right.roleCode) ||
    request.appointmentType.providerTypes.indexOf(left.roleCode) -
      request.appointmentType.providerTypes.indexOf(right.roleCode) ||
    (left.practitionerRole.id ?? '').localeCompare(right.practitionerRole.id ?? '')
  );
}

function providerKeyForTarget(target: SchedulingTarget): string {
  return `${target.actor.resourceType}/${target.actor.id}`;
}

function proposedAppointmentKey(appointment: ProposedAppointment, target: SchedulingTarget): string {
  const service = `HealthcareService/${target.healthcareService.id}`;
  const provider = providerKeyForTarget(target);
  const start = parseFhirDateTime(appointment.start ?? '').getTime();
  const end = parseFhirDateTime(appointment.end ?? '').getTime();
  return `${start}|${end}|${service}|${provider}`;
}

function getProviderTimezone(practitioner: Practitioner): string {
  const value = getTimezoneExtensionValue(practitioner.extension);
  if (!value || !isValidTimezone(value)) {
    throw new SchedulingError(
      SCHEDULING_ERROR_CATEGORIES.timezoneMissing,
      `Practitioner/${practitioner.id} has no scheduling timezone.`
    );
  }
  return value;
}

async function collectResourcePages<T>(pages: AsyncIterable<T[]>): Promise<T[]> {
  const resources: T[] = [];
  for await (const page of pages) {
    resources.push(...page);
  }
  return resources;
}

async function resolvePolicyDate(
  value: Date | string | Promise<Date | string> | undefined,
  fallback: Date,
  name: string
): Promise<Date> {
  const resolved = value === undefined ? fallback : await value;
  const date = resolved instanceof Date ? new Date(resolved) : parseFhirDateTime(resolved);
  if (Number.isNaN(date.getTime())) {
    throw new SchedulingError(SCHEDULING_ERROR_CATEGORIES.configurationInvalid, `Availability ${name} is invalid.`);
  }
  return date;
}
